// Lógica pura del armado manual.
//
// Vive separada del componente por un motivo concreto: se puede probar
// con `node`, sin navegador y sin compilar nada.
//
// Está escrito en CommonJS (`module.exports`) y no en ESM a propósito.
// Los tests del proyecto corren con `node archivo.js` y sin paso de
// compilación —esbuild no es dependencia, se usa vía npx solo para
// verificar—, así que un módulo con `export` sería imposible de probar
// igual que el resto. Webpack interopera CommonJS sin problema, así que
// `import { alternarSeleccion } from "./seleccion"` funciona igual.

/**
 * Qué queda seleccionado al tocar una prenda.
 *
 * UNA SOLA REGLA: tocar suma, tocar de nuevo saca.
 *
 * ─────────────────────────────────────────────────────────────
 * Antes esta función reemplazaba por zona: elegir una segunda camisa
 * sacaba la primera, porque el motor de combinaciones exige cubrir
 * cada zona exactamente una vez.
 *
 * Estaba mal, y el usuario lo señaló: **eso impide superponer**. Una
 * remera con una camisa abierta encima son dos prendas de torso, y es
 * una forma de vestirse perfectamente normal. Lo mismo un chaleco
 * sobre una camisa, o dos pares de medias en invierno.
 *
 * La confusión de fondo era mía: apliqué acá una regla que existe
 * para el MOTOR. El motor necesita "cada zona una vez" porque
 * enumera combinaciones y sin esa restricción el espacio de búsqueda
 * explota. Pero esta pantalla no enumera nada — registra lo que la
 * persona efectivamente se puso. Y lo que se puso no lo decide una
 * restricción de algoritmo.
 *
 * La regla de esta pantalla siempre fue "acá no hay reglas". Faltaba
 * que la selección lo cumpliera.
 * ─────────────────────────────────────────────────────────────
 */
function alternarSeleccion(elegidas, prenda) {
  if (elegidas.includes(prenda.id)) {
    return elegidas.filter((x) => x !== prenda.id);
  }
  return [...elegidas, prenda.id];
}

/**
 * Las prendas de un outfit del historial que todavía existen.
 *
 * Un outfit viejo puede referenciar una prenda borrada. Repetirlo con
 * ese id metería un fantasma que el servidor después rechaza, y el
 * usuario vería un error sin entender de dónde salió.
 */
function prendasQueSiguenVivas(outfit, porId) {
  return (outfit?.prenda_ids || []).filter((id) => porId.has(id));
}

/**
 * Filtra por tipo de prenda.
 *
 * El tipo es un DATO de la prenda, no una regla: filtrar por "Superior"
 * acota lo que se ve, no lo que se puede elegir. Es la diferencia con
 * los grupos por zona que había antes, que además de organizar
 * limitaban.
 */
// Sin tildes y en minúsculas, para que "marron" encuentre "Marrón".
// Es la misma normalización que hace el servidor en su búsqueda: si el
// cliente buscara distinto que el servidor, la misma consulta daría
// resultados distintos según qué pantalla la haga.
function sinTildes(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function filtrarPrendas(prendas, { texto = "", tipo = null } = {}) {
  const busca = sinTildes(texto).trim();

  return (prendas || []).filter((p) => {
    if (tipo && p.tipo !== tipo) return false;
    if (!busca) return true;

    // Se busca en TODO lo que la prenda dice de sí misma, no solo en el
    // nombre. Uno busca "denim" o "cuero" tanto como "jean": buscar
    // solo por nombre obliga a recordar cómo la llamaste, que es
    // justamente lo que no se recuerda parado frente al placard.
    //
    // Las notas quedan afuera a propósito: ahí viven las marcas
    // internas de la migración (notion:, foto_vieja:) y buscar "notion"
    // devolvería el armario entero.
    const dónde = [
      p.nombre,
      p.tipo,
      p.material,
      ...(p.colores || []),
      ...(p.categorias || []),
      p.talla,
    ];

    return dónde.some((campo) => sinTildes(campo).includes(busca));
  });
}

/**
 * Las prendas que pueden reemplazar a otra dentro de un outfit.
 *
 * El criterio es la FIRMA DE ZONAS, no el tipo. Una prenda que cubre
 * torso solo se puede cambiar por otra que cubra torso solo: si se
 * permitiera cambiar una camisa por un enterizo, el outfit pasaría a
 * cubrir piernas dos veces sin que nadie lo pidiera.
 *
 * Los abrigos y accesorios no cubren zona, así que su firma es la misma
 * (vacía) y se confundirían entre sí. Para esos se compara además el
 * tipo: cambiar la campera ofrece camperas, no gorras.
 *
 * `enUso` son los ids que ya están en el outfit: ofrecer una prenda que
 * ya tenés puesta sería ofrecer un duplicado.
 */
function firmaZonas(prenda) {
  return [...(prenda?.cubre || [])].sort().join("+");
}

function alternativasPara(prenda, armario, enUso = []) {
  if (!prenda) return [];

  const firma = firmaZonas(prenda);
  const sinZona = firma === "";
  const usados = new Set(enUso);

  return (armario || []).filter((p) => {
    if (p.id === prenda.id) return false;
    if (usados.has(p.id)) return false;
    if (firmaZonas(p) !== firma) return false;
    // Sin zona, la firma vacía no distingue un abrigo de una gorra.
    if (sinZona && p.tipo !== prenda.tipo) return false;
    return true;
  });
}

/**
 * Aplica los cambios de un outfit sobre sus prendas originales.
 *
 * Se guarda el mapa `original -> reemplazo` en vez de la lista final
 * para poder decir "esto lo cambiaste vos" y ofrecer volver atrás. Con
 * solo la lista final, el outfit editado sería indistinguible de uno
 * que salió así del motor.
 */
function aplicarCambios(prendas, cambios) {
  if (!cambios) return prendas || [];
  /* `null` quiere decir SACADA, y por eso se pregunta por la clave y no
   * por el valor.
   *
   * ⚠️  LA VERSIÓN ANTERIOR ERA `cambios[p.id] || p`, y con esa línea
   *     una prenda sacada volvía sola: `null || p` es `p`. El "sacar"
   *     no habría dado ningún error — la prenda simplemente seguía ahí,
   *     que es la forma más molesta de fallar porque parece que el
   *     botón no anda. */
  return (prendas || [])
    .map((p) => (Object.prototype.hasOwnProperty.call(cambios, p.id) ? cambios[p.id] : p))
    .filter(Boolean);
}

/**
 * De la prenda que se está VIENDO al id de la que estaba ORIGINALMENTE
 * en ese lugar.
 *
 * EL BUG QUE ESTO ARREGLA (2026-08-20)
 *
 * Los cambios se guardan como `original -> reemplazo`, pero la pantalla
 * muestra el reemplazo. Al cambiar la misma prenda por segunda vez, el
 * cambio se guardaba bajo el id del REEMPLAZO — que no está en la lista
 * original — así que `aplicarCambios` no lo encontraba nunca y el
 * segundo cambio no hacía nada. En silencio: elegías otra camisa, el
 * modal se cerraba, y la tarjeta seguía igual.
 *
 * Antes casi no pasaba porque el botón de cambiar era un círculo de
 * 26px que nadie encontraba. Con "Editar" —que invita a cambiar una,
 * dos o las tres— pasa a ser el camino normal.
 */
function idOriginalDe(cambios, idMostrado) {
  if (!cambios) return idMostrado;
  for (const [original, reemplazo] of Object.entries(cambios)) {
    if (reemplazo?.id === idMostrado) return original;
  }
  return idMostrado;
}

/**
 * Registra un cambio sobre el conjunto de cambios ya hechos.
 *
 * Devuelve el mapa nuevo. Si la prenda elegida es la que estaba
 * originalmente, el cambio se BORRA en vez de guardarse como
 * "original -> original": así la tarjeta deja de decir "Editado"
 * cuando volviste al punto de partida, que es la verdad.
 */
function registrarCambio(cambios, mostrada, nueva) {
  const previos = cambios || {};
  const original = idOriginalDe(previos, mostrada.id);
  const siguiente = { ...previos };

  /* ⚠️  `String()` EN LOS DOS LADOS (2026-08-24, ME-11).
   *
   * `original` sale de `Object.entries(cambios)`, así que es SIEMPRE un
   * string. `nueva.id` es el id de la prenda, que es un número.
   *
   * `3 === "3"` es false. Consecuencia: después del SEGUNDO cambio
   * sobre la misma prenda, volver a la original NO borraba la entrada, y
   * la tarjeta quedaba diciendo "Editado · deshacer" para siempre —
   * aunque el outfit se registrara bien, porque `aplicarCambios` mapea
   * por coerción.
   *
   * O sea: la app hacía lo correcto y decía lo incorrecto.
   */
  /* `nueva` en null es SACAR la prenda. No hay id contra el cual
   * comparar, así que se guarda el null derecho: la comparación de
   * abajo solo tiene sentido cuando hay una prenda de reemplazo. */
  if (nueva === null) {
    siguiente[original] = null;
    return siguiente;
  }

  if (String(nueva.id) === String(original)) delete siguiente[original];
  else siguiente[original] = nueva;

  return siguiente;
}

module.exports = {
  alternarSeleccion,
  prendasQueSiguenVivas,
  filtrarPrendas,
  firmaZonas,
  alternativasPara,
  aplicarCambios,
  idOriginalDe,
  registrarCambio,
};
